/**
 * Integration Connection Card Component
 *
 * Displays integration connection status with logo, name, and connect button
 * Includes live status checking and connection popup handling
 */

import { FC, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Loader2, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react'
import { useExtensionConnection } from '@/hooks/use-extension-connection'
import { ExtensionIcon } from './extension-icon'
import { cn } from '@/lib/utils'

interface ExtensionConnectionCardProps {
  extensionId: string
  extensionName: string
  description?: string
  icon: string
  toolkitSlug?: string
  officialLogo?: string
  onConnectionSuccess?: () => void
  onConnectionError?: (error: string) => void
}

export const ExtensionConnectionCard: FC<ExtensionConnectionCardProps> = ({
  extensionId,
  extensionName,
  description,
  icon,
  toolkitSlug,
  officialLogo,
  onConnectionSuccess,
  onConnectionError
}) => {
  const [isConnecting, setIsConnecting] = useState(false)

  const { status, openConnectionPopup, disconnect, refetch } = useExtensionConnection({
    extensionId,
    toolkitSlug: toolkitSlug || icon,
    pollInterval: 3000,
    autoStart: true
  })

  // Handle connection success callback
  useEffect(() => {
    if (status.isConnected && !status.isLoading && onConnectionSuccess) {
      onConnectionSuccess()
    }
  }, [status.isConnected, status.isLoading, onConnectionSuccess])

  // Handle connection error callback
  useEffect(() => {
    if (status.error && onConnectionError) {
      onConnectionError(status.error)
    }
  }, [status.error, onConnectionError])

  const handleConnect = async () => {
    setIsConnecting(true)
    try {
      await openConnectionPopup()
    } finally {
      setIsConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    await disconnect()
  }

  const handleRefresh = () => {
    refetch()
  }

  return (
    <Card className="mx-auto w-full max-w-md">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          {/* Integration Logo */}
          <div className="shrink-0">
            {officialLogo ? (
              <img
                src={officialLogo}
                alt={`${extensionName} logo`}
                width={48}
                height={48}
                className="rounded-lg"
                onError={(e) => {
                  // Fallback to the local integration icon if official logo fails
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  const fallback = target.nextElementSibling as HTMLElement
                  if (fallback) fallback.style.display = 'block'
                }}
              />
            ) : null}
            <div style={{ display: officialLogo ? 'none' : 'block' }}>
              <ExtensionIcon name={icon} size={48} className="rounded-lg" />
            </div>
          </div>

          {/* Integration Info */}
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <h3 className="text-foreground truncate text-lg font-semibold">{extensionName}</h3>

              {/* Connection Status Badge */}
              {status.isLoading ? (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Loader2 className="size-3 animate-spin" />
                  Checking...
                </Badge>
              ) : status.isConnected ? (
                <Badge
                  variant="default"
                  className="flex items-center gap-1 border-green-300 bg-green-100 text-green-800"
                >
                  <CheckCircle className="size-3" />
                  Connected
                </Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className="flex items-center gap-1 border-gray-300 bg-gray-100 text-gray-700"
                >
                  <AlertCircle className="size-3" />
                  Not Connected
                </Badge>
              )}
            </div>

            {description && (
              <p className="text-muted-foreground mb-3 line-clamp-2 text-sm">{description}</p>
            )}

            {/* Connection Details */}
            {status.isConnected && status.count > 0 && (
              <p className="text-muted-foreground mb-3 text-xs">
                {status.count} active connection{status.count > 1 ? 's' : ''}
              </p>
            )}

            {status.error && <p className="mb-3 text-xs text-red-600">Error: {status.error}</p>}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {status.isConnected ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDisconnect}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    Disconnect
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefresh}
                    className="text-muted-foreground"
                  >
                    Refresh
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleConnect}
                  disabled={isConnecting || status.isLoading}
                  className={cn(
                    'font-medium transition-all duration-200',
                    'border border-blue-600 bg-blue-600 hover:bg-blue-700',
                    'rounded-lg px-4 py-2 text-white',
                    'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                    (isConnecting || status.isLoading) && 'cursor-not-allowed opacity-50'
                  )}
                >
                  {isConnecting || status.isLoading ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      {isConnecting ? 'Connecting...' : 'Checking...'}
                    </>
                  ) : (
                    <>
                      <ExternalLink className="mr-2 size-4" />
                      Connect Your Account
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
